import {
  light,
  regular,
  solid,
} from '@fortawesome/fontawesome-svg-core/import.macro'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'

import { Button, Card, Icon, Link, Text, Dialog, List, CardColors } from '@/ui'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { getUnpaidTaxEstimatesByYear } from '@/features/taxes/quarterly-tax-estimates/userTaxEstimates.selector'
import { PreviousTaxPayments } from '@/features/taxes/taxes-profile/form-flow/PreviousTaxPayments'
import { splitTaxYearQuarter } from '@/features/taxes/quarterly-tax-estimates/helpers'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { dateWithoutTimestamp } from '@/features/taxes/quarterly-tax-estimates/service'

import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { ViewMissingStatementsAccordion } from '@/components/ViewMissingStatementsAccordion'
import { TextColors, TypographyStyles } from '@/styles'
import {
  FETCH_NEEDED_BANK_STATEMENTS_FOR_CURRENT_TAX_QUARTER,
  getNeededBankStatementsForCurrentTaxQuarter,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'

// TODO: Migrate to V2
import {
  selectActiveQuarterlyTaxEstimateDetails,
  selectHasUserStartedQteWizardThisQuarter,
  selectIsPastTaxEstimateCalculationDate,
} from '@/featuresV1/Admin/QuarterlyTaxEstimateDetails/quarterlyTaxEstimateDetails.selector'

import { Loader } from '@/src/components/BaseComponents'

const checklistItemIconClasses = (pastDue: boolean) => {
  return `${pastDue ? TextColors.DANGER : TextColors.MOSS} min-h-[50px] min-w-[50px] mr-4`
}
const addressCardIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={light('address-card')}
  />
)
const quarterIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={light('circle-quarter-stroke')}
  />
)
const bookIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={light('book-open-cover')}
  />
)
const uploadIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={light('cloud-upload')}
  />
)
export const ChecklistItemCard = ({
  isDone,
  icon,
  header,
  isStarted,
  children,
  onClick,
}: {
  isDone: boolean
  icon: React.ReactNode
  header: React.ReactNode
  isStarted?: boolean
  children?: React.ReactNode
  onClick?: () => void
}) => {
  return (
    <Card color={isDone ? CardColors.LEVEL_0 : CardColors.LEVEL_1}>
      <div className="flex gap-4 items-center">
        <div>{icon}</div>
        <div className="flex-1">
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
            <div className="flex flex-col">
              <Text variant={TypographyStyles.H3} className="mb-2">
                {header}
              </Text>
              {children}
            </div>
            <div className="w-full sm:w-40">
              <Button
                variant={isDone ? 'secondary' : 'primary'}
                disabled={isDone}
                onClick={onClick}
                className="w-full"
              >
                {isDone ? (
                  <>
                    <Icon icon={light('check')} className="mr-2" /> Done
                  </>
                ) : isStarted ? (
                  'Continue'
                ) : (
                  'Start'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export const DeadlineText = ({
  isDone,
  onEditClick,
}: {
  isDone: boolean
  onEditClick: () => void
}) => {
  const isPastCalculationDate = useReselector(
    selectIsPastTaxEstimateCalculationDate
  )
  if (isPastCalculationDate) return null
  return isDone ? (
    <Text color={TextColors.MOSS}>
      <Button onClick={onEditClick} variant="link">
        Edit
      </Button>{' '}
      income and withholdings information
    </Text>
  ) : (
    <Text color={TextColors.MOSS}>You’ll need your most recent paystub.</Text>
  )
}

export const UpdateTaxInfoCTA = ({ isDone }: { isDone: boolean }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const started = useReselector(selectHasUserStartedQteWizardThisQuarter)
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)

  const onEditClick = () => {
    dispatch(
      updateFinancialProfile(fp?.id, {
        qteWizardLastStartedAt: dateWithoutTimestamp().format(
          DATE_FORMATS.TIMESTAMP
        ),
      })
    )
    navigate('/taxes/profile')
  }

  return (
    <ChecklistItemCard
      isDone={isDone}
      isStarted={started}
      icon={addressCardIcon(isPastCalc)}
      header="Update your income and withholdings"
      onClick={onEditClick}
    >
      <DeadlineText onEditClick={onEditClick} isDone={isDone} />
    </ChecklistItemCard>
  )
}

export const UpdateQTEPaymentsCTA = ({ isDone }: { isDone: boolean }) => {
  const [open, setOpen] = useState(false)
  const activeQuarter = useReselector(selectActiveQuarterlyTaxEstimateDetails)
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)
  const unpaidQuarters = useReselector(
    getUnpaidTaxEstimatesByYear,
    activeQuarter?.taxYear
  ).reduce((s, e) => s.add(e.taxQuarter), new Set<string>())
  const formattedEstimates = useMemo(() => {
    if (!activeQuarter) return null
    return (
      <List bulleted>
        {[...unpaidQuarters].map((quarterKey) => {
          const { quarter, year } = splitTaxYearQuarter(quarterKey)
          return (
            <List.Item key={quarterKey}>
              <Text color={TextColors.MOSS}>
                <b>
                  Q{quarter} {year}
                </b>
              </Text>
            </List.Item>
          )
        })}
      </List>
    )
  }, [activeQuarter, unpaidQuarters])

  return (
    <>
      <ChecklistItemCard
        isDone={isDone}
        icon={quarterIcon(isPastCalc)}
        header="Enter your quarterly tax payments"
        onClick={() => setOpen(true)}
      >
        {!isDone && (
          <>
            <Text color={TextColors.MOSS}>
              Enter payments for the following quarters:
            </Text>
            <div className="ml-2 mt-2">{formattedEstimates}</div>
          </>
        )}
        <DeadlineText onEditClick={() => setOpen(true)} isDone={isDone} />
      </ChecklistItemCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <Dialog.Content className="pt-5 max-w-2xl">
          <PreviousTaxPayments />
          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button>Close</Button>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export const UploadNeededBankStatementsCTA = () => {
  const track = useAnalyticsTrack()
  const needed = useFetchResponse(getNeededBankStatementsForCurrentTaxQuarter)
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)
  const total = needed?.reduce((acc, s) => acc + s.statements.length, 0) ?? 0
  const isDone = Boolean(needed && total === 0)
  const navigate = useNavigate()
  const fetching = useReselector(selectIsFetchingForKeys, [
    FETCH_NEEDED_BANK_STATEMENTS_FOR_CURRENT_TAX_QUARTER,
  ])

  const content = useMemo(() => {
    if (fetching) return <Loader />
    if (isDone) {
      return (
        <Text>
          Great job! You are all caught up.{' '}
          <Link href="/practice/documents?tab=bank" variant="secondary">
            View Your Documents
          </Link>
        </Text>
      )
    }
    return (
      <>
        <Text>Upload missing statements</Text>
        <Text>
          Help us complete your bookkeeping by uploading missing <b>business</b>{' '}
          bank statements.{' '}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard"
            variant="secondary"
            newPage
          >
            Why?
          </Link>
        </Text>
        <Text>
          <b>
            {total} statement{total !== 1 ? 's' : ''}
          </b>{' '}
          {total !== 1 ? 'are' : 'is'} missing
        </Text>
        <ViewMissingStatementsAccordion
          title="View list of missing statements"
          neededStatements={needed}
        />
      </>
    )
  }, [fetching, isDone, total, needed])

  return (
    <ChecklistItemCard
      isDone={isDone}
      icon={uploadIcon(isPastCalc)}
      header="Upload your statements"
      onClick={() => {
        track('QTE Upload Bank Statements CTA Clicked')
        navigate('/practice/documents?tab=bank')
      }}
    >
      {content}
    </ChecklistItemCard>
  )
}

export const ReviewTxnCTA = ({
  isDone,
  count,
}: {
  isDone: boolean
  count: number
}) => {
  const navigate = useNavigate()
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)
  return (
    <ChecklistItemCard
      isDone={isDone}
      icon={bookIcon(isPastCalc)}
      header="Review your transactions"
      onClick={() => navigate('/transactions/review')}
    >
      <Text color={TextColors.MOSS}>
        {isDone ? (
          <>
            You&apos;re all caught up.{' '}
            <Link href="/transactions/all" variant="secondary">
              View transactions
            </Link>
          </>
        ) : (
          <>
            Get the most accurate estimate by making sure your transactions are
            up-to-date.
            <br />
            <b>
              {count} transaction{count !== 1 ? 's' : ''}
            </b>{' '}
            need{count === 1 ? 's' : ''} a note/categorization.
          </>
        )}
      </Text>
    </ChecklistItemCard>
  )
}

export const BooksStatus = ({
  booksReconciled,
}: {
  booksReconciled: boolean
}) => {
  return (
    <Card color={CardColors.LEVEL_1} className="px-8 py-2">
      <Text color={TextColors.MOSS} className="flex items-center">
        <Icon
          icon={
            booksReconciled ? regular('book-heart') : regular('book-open-cover')
          }
          className={`min-w-[16px] min-h-[16px] mr-2 ${booksReconciled ? TextColors.GREEN : TextColors.YELLOW}`}
        />
        Your bookkeeper has indicated your books{' '}
        {booksReconciled ? (
          <>
            have been{' '}
            <span className="text-green-600 font-bold"> reviewed.</span>
          </>
        ) : (
          <>
            are still{' '}
            <span className="text-yellow-600 font-bold"> in review.</span>
          </>
        )}
      </Text>
    </Card>
  )
}

export const BooksIncompleteInfo = () => (
  <Card color={CardColors.LEVEL_1} className="p-4">
    <div className="flex items-start gap-4">
      <Icon
        icon={solid('info-circle')}
        className="min-w-[20px] min-h-[20px] mt-1 text-foreground"
      />
      <div>
        <Text variant={TypographyStyles.H3} className="mb-2">
          I&apos;ve clarified my transactions. Why are my books still in review?
        </Text>
        <List bulleted>
          <List.Item>
            <Text>
              If your bookkeeper needs more information, they&apos;ll reach out
              via Messages
            </Text>
          </List.Item>
          <List.Item>
            <Text>
              If you&apos;ve completed all of the steps, your bookkeeper may
              need more time to categorize your transactions
            </Text>
          </List.Item>
        </List>
      </div>
    </div>
  </Card>
)
